import {
  AllowedFilesEntities,
  Charge,
  CustomLabel,
  DEFAULT_ACCEPTED_MIME_TYPES,
  DocumentCategoryWithoutReadIdAndClientId,
  EntityType,
  FileCategory,
  File as FileModel,
  FolderEntity,
  MimeTypes,
  ROOT_FOLDER_ID,
  UNIT_OWNER_TABLE_NAME,
  UploadFile,
  calculateUniqueCharges,
  createDeterministicFolderId,
  fromDateTime,
  getTableClientId,
  getUniqueCategoryNameParameter,
  getUniqueFileName,
  isGeneratedUblDocument,
  isNilOrEmpty,
  todayDateTime,
} from '@rentguru/commons-utils';
import { UpdateSpec } from 'dexie';
import { compact, isEmpty, isNil, last } from 'lodash';
import { IntlFormatters } from 'react-intl';

import { getFolderSize } from 'src/components/FoldersWithDB/Components/utils/folderUtils';
import { getDocumentCategoryFolder } from 'src/components/FoldersWithDB/Components/utils/folderWithDBUtils';
import {
  ConfirmationDialogContext,
  ConfirmationDialogOptions,
  DialogConfig,
} from 'src/hooks/ConfirmationDialogContext';
import { fetchUnitOwnersForFolders } from 'src/hooks/ContactsContext';
import { FileCategoryContext } from 'src/hooks/FileCategoryContext';
import { FilesContext } from 'src/hooks/FilesContext';
import { fetchInvoices } from 'src/hooks/InvoicesContext';
import { SignatureDocumentsContext } from 'src/hooks/SignatureDocumentContext';
import { TechnicsContext } from 'src/hooks/TechnicsContext';

import { calculateFoldersToAdd, getFolderToAddWithTargetFolder } from '../WebWorkers/utils/folderSyncingUtils';

import { FolderDatabaseContext } from './FolderDatabaseContext';
import { FolderWithDBContext } from './FolderWithDBContext';

export interface UniqueFolderNameAndUploadFilesInput {
  filesToUpload: File[];
  filesInFolder: FolderDatabaseModel[];
  entityType: EntityType;
  entityId: string;
  currentCategory: DocumentCategoryWithoutReadIdAndClientId;
  skipCheck: boolean;
  createFile: FilesContext['createFile'];
  deleteFile: FilesContext['deleteFile'];
  fetchFileFromId: FilesContext['fetchFileFromId'];
  getConfirmation: ConfirmationDialogContext['getConfirmation'];
  formatMessage: IntlFormatters['formatMessage'];
  getSignatureDocumentForFile: SignatureDocumentsContext['getSignatureDocumentForFile'];
  setUploadFiles: FolderWithDBContext['setUploadFiles'];
}

export enum FolderIndexes {
  DEPTH = 'depth',
  ENTITY_ID = 'entityId',
  DOCUMENT_CATEGORY_ID = 'documentCategoryId',
  DOCUMENT_ID = 'documentId',
}

export enum FolderDatabaseAction {
  MOVE,
  RENAME,
  CREATE,
  UPDATE,
  DELETE,
}

export interface BulkUpdateFolder {
  key: string;
  changes: UpdateSpec<FolderDatabaseModel>;
}

export interface FolderDatabaseModel {
  id: string;
  depth: string;
  depthByFolderId: string[];
  depthString: (string | CustomLabel[])[];
  labels?: CustomLabel[];
  translatedLabel?: string;
  contactLabelToSortOn?: string;
  folderEntity: FolderEntity;
  entityId: string;
  documentId?: string;
  mimeType?: string;
  documentCategoryId: string;
  categoryType: FileCategory;
  categoryEntity: string;
  size?: number;
  updatedAt?: string;
  isInvoicePaid?: boolean;
}

export const removeDuplicatesById = <T extends { id: string }>(items: T[]): T[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) {
      return false;
    }
    seen.add(item.id);
    return true;
  });
};

export const getFolderDepthStringProp = (folder: FolderDatabaseModel): string | CustomLabel[] => {
  if (folder.folderEntity === FolderEntity.FOLDER) {
    return folder.labels!;
  }
  return folder.translatedLabel!;
};

export const getFolderDepthForNavigation = (folder?: FolderDatabaseModel): string => {
  if (!folder) {
    return ROOT_FOLDER_ID;
  }
  if (folder.folderEntity === FolderEntity.FOLDER) {
    return `${folder.depth}.${folder.documentCategoryId}`;
  }
  if (folder.folderEntity === FolderEntity.JOINT_CONTACT) {
    const depthParts = folder.depth.split('.');
    depthParts.pop();
    return `${depthParts.join('.')}.${folder.entityId}`;
  }
  return `${folder.depth}.${folder.entityId}`;
};

export const getUniqueCategoryParameter = (
  labels: CustomLabel[],
  currentLevelCategories: DocumentCategoryWithoutReadIdAndClientId[]
): CustomLabel[] => {
  const uniqueParameter = getUniqueCategoryNameParameter(currentLevelCategories, labels);
  const mappedLabels = labels?.map((label) => {
    return {
      ...label,
      label: uniqueParameter !== 0 ? `${label.label} (${uniqueParameter})` : label.label,
    };
  });
  return mappedLabels;
};

export const getUniqueFolderNameAndCreateFiles = async ({
  filesToUpload,
  filesInFolder,
  entityType,
  entityId,
  currentCategory,
  skipCheck,
  createFile,
  deleteFile,
  fetchFileFromId,
  getConfirmation,
  formatMessage,
  getSignatureDocumentForFile,
  setUploadFiles,
}: UniqueFolderNameAndUploadFilesInput): Promise<(FileModel | null)[]> => {
  const uniqueFiles: File[] = [];
  const filesToDelete: FileModel[] = [];
  const uploadFiles: UploadFile[] = [];

  const fileNamesInFolder = compact(filesInFolder.map((file) => file.translatedLabel));
  for (const file of filesToUpload) {
    const fileType = file.type.split('/')[0];
    if (DEFAULT_ACCEPTED_MIME_TYPES.includes(file.type as MimeTypes) || fileType === 'image') {
      let uniqueFileName = file.name;
      let canDeleteFile = false;
      let error = false;
      if (filesInFolder.some((fileFolder) => fileFolder.translatedLabel === file.name) && !skipCheck) {
        const result = await getConfirmation({
          title: formatMessage({ id: 'folders.fileActions.duplicateFolderTitle' }),
          text: formatMessage({ id: 'folders.fileActions.duplicateFolder' }, { fileName: file.name }),
          actionButtonLabel: formatMessage({ id: 'folders.fileActions.replace' }),
          thirdButtonLabel: formatMessage({ id: 'folders.fileActions.keepBoth' }),
          formatMessage,
        } as DialogConfig);
        if (result === ConfirmationDialogOptions.THIRD_BUTTON) {
          uniqueFileName = getUniqueFileName(fileNamesInFolder, file.name);
        }

        if (result === ConfirmationDialogOptions.ACTION_BUTTON) {
          const currentFile = filesInFolder.find((fileInFolder) => fileInFolder.translatedLabel === file.name);
          const hasSignature = getSignatureDocumentForFile(currentFile!.documentId!);
          if (currentFile && hasSignature) {
            error = true;
          } else {
            canDeleteFile = true;
          }
        }
        if (isNil(result)) {
          continue;
        }
      }
      if (!error) {
        uploadFiles.push({ fileName: uniqueFileName, fileExtension: file.type, progression: 0 });
        const encodeFile = new File([file], encodeURIComponent(uniqueFileName), {
          type: file.type,
          lastModified: file.lastModified,
        });
        uniqueFiles.push(encodeFile!);
        if (canDeleteFile) {
          const folderToDelete = filesInFolder.find((fileInFolder) => fileInFolder.translatedLabel === file.name);
          const fileToDelete = await fetchFileFromId(folderToDelete!.documentId!);
          filesToDelete.push(fileToDelete[0]);
        }
      }
    }
  }
  setUploadFiles(uploadFiles);
  await Promise.all(filesToDelete.map((fileToDelete) => deleteFile(fileToDelete)));
  const promises = uniqueFiles.map((uploadFile) => {
    return createFile(uploadFile, entityType, entityId, currentCategory.id, {
      onProgress: ({ transferredBytes, totalBytes }: { transferredBytes: number; totalBytes: number }) => {
        if (totalBytes) {
          const progression = Math.round((transferredBytes / totalBytes) * 100);
          const downloadFile = uploadFiles.find(
            (downloadFileToFind) => downloadFileToFind.fileName === decodeURIComponent(uploadFile.name)
          );
          if (downloadFile) {
            downloadFile.progression = progression;
            setUploadFiles([...uploadFiles]);
          }
        }
      },
    });
  });
  return await Promise.all(promises);
};

export const calculateEmptyFolders = (
  lastNavigationParentFolder: FolderDatabaseModel,
  fileCategories: DocumentCategoryWithoutReadIdAndClientId[],
  currentFolders: FolderDatabaseModel[]
) => {
  if (isNil(lastNavigationParentFolder)) {
    return [];
  }
  const emptyCategories = fileCategories.reduce<DocumentCategoryWithoutReadIdAndClientId[]>((acc, category) => {
    if (
      category.entity !== lastNavigationParentFolder.categoryEntity ||
      (lastNavigationParentFolder.folderEntity === FolderEntity.FOLDER &&
        category.parentId !== lastNavigationParentFolder.documentCategoryId) ||
      (lastNavigationParentFolder.folderEntity !== FolderEntity.FOLDER && !isNil(category.parentId))
    ) {
      return acc;
    }

    const isNotInCurrentFolders = !currentFolders.some((folder) => folder.documentCategoryId === category.id);

    if (isNotInCurrentFolders) {
      acc.push(category);
    }

    return acc;
  }, []);
  const foldersToAdd: FolderDatabaseModel[] = emptyCategories.map((category) => {
    const folderToAddId = createDeterministicFolderId(
      FolderEntity.FOLDER,
      lastNavigationParentFolder!.entityId,
      category.id
    );
    return {
      id: folderToAddId,
      depth: getFolderDepthForNavigation(lastNavigationParentFolder),
      depthString: [
        ...(lastNavigationParentFolder.depthString ?? []),
        lastNavigationParentFolder.translatedLabel ?? lastNavigationParentFolder.labels ?? [],
      ],
      depthByFolderId: checkDepthByFolderId([
        ...lastNavigationParentFolder.depthByFolderId,
        lastNavigationParentFolder.id,
        folderToAddId,
      ]),
      labels: category.labels,
      folderEntity: FolderEntity.FOLDER,
      entityId: lastNavigationParentFolder!.entityId,
      documentCategoryId: category.id,
      categoryType: category.fileCategory,
      categoryEntity: category.entity ?? '',
      size: 0,
    };
  });
  return foldersToAdd;
};

export const syncFolderMove = async (
  currentCategoryFolder: FolderDatabaseModel,
  category: DocumentCategoryWithoutReadIdAndClientId,
  getFoldersWithCustomFilter: FolderDatabaseContext['getFoldersWithCustomFilter'],
  getFoldersByIndex: FolderDatabaseContext['getFoldersByIndex'],
  getDocumentCategoryById: FileCategoryContext['getDocumentCategoryById']
) => {
  const foldersToAdd: FolderDatabaseModel[] = [];
  const foldersToUpdate: BulkUpdateFolder[] = [];
  const oldParentFoldersToCheck: FolderDatabaseModel[] = [];
  const entityDepth = getFolderEntityDepth(currentCategoryFolder, getDocumentCategoryById);
  const parentDepthId = last(entityDepth.split('.'));
  const oldCategoryId = last(currentCategoryFolder.depth.split('.'));
  const isOldParentACategory = !isNil(getDocumentCategoryById(oldCategoryId ?? ''));
  const oldParentFolders = await getFoldersWithCustomFilter((folder) => {
    const isInEntity = getFolderDepthForNavigation(folder).includes(entityDepth);
    if (isOldParentACategory) {
      return folder.documentCategoryId === oldCategoryId && folder.folderEntity !== FolderEntity.FILE && isInEntity;
    }
    return (
      folder.entityId === parentDepthId &&
      ![FolderEntity.FILE, FolderEntity.FOLDER].includes(folder.folderEntity) &&
      isInEntity
    );
  });
  const parentFolders: FolderDatabaseModel[] = await getFoldersWithCustomFilter(
    (folder) =>
      folder.documentCategoryId === (category.parentId ?? '') &&
      folder.folderEntity !== FolderEntity.FILE &&
      folder.entityId === currentCategoryFolder.entityId &&
      getFolderDepthForNavigation(folder).includes(entityDepth)
  );
  if (isEmpty(parentFolders)) {
    const parentCategory = getDocumentCategoryById(category.parentId ?? '');
    const currentEntityItems = await getFoldersByIndex(FolderIndexes.ENTITY_ID, currentCategoryFolder.entityId);
    const { categoryDepth, folderCategory } = await getCategoryDepthToAdd(
      parentCategory,
      currentEntityItems,
      [],
      getDocumentCategoryById
    );
    const foldersToWorkWith = !isNilOrEmpty(folderCategory)
      ? folderCategory
      : currentEntityItems.filter(
          (entityFolder) => ![FolderEntity.FOLDER, FolderEntity.FILE].includes(entityFolder.folderEntity)
        );
    if (isEmpty(categoryDepth)) {
      categoryDepth.push(parentCategory);
    }
    for (const folderToWorkWithIndex in foldersToWorkWith) {
      const folderToWorkWith = foldersToWorkWith[folderToWorkWithIndex];
      for (const categoryFromDepth of categoryDepth) {
        const categoryFolder = await getDocumentCategoryFolder(
          folderToWorkWith?.entityId ?? '',
          categoryFromDepth.id,
          getFoldersWithCustomFilter
        );
        if (categoryFolder) {
          foldersToWorkWith[folderToWorkWithIndex] = categoryFolder;
          parentFolders[folderToWorkWithIndex] = categoryFolder;
          continue;
        }

        const folderId = createDeterministicFolderId(
          FolderEntity.FOLDER,
          folderToWorkWith?.entityId ?? 'root',
          categoryFromDepth.id
        );
        const newFolder: FolderDatabaseModel = {
          id: folderId,
          documentCategoryId: categoryFromDepth.id,
          categoryType: parentCategory.fileCategory,
          categoryEntity: parentCategory.entity ?? '',
          depth: getFolderDepthForNavigation(folderToWorkWith),
          depthString: [...(folderToWorkWith?.depthString || []), getFolderDepthStringProp(folderToWorkWith)],
          depthByFolderId: checkDepthByFolderId([...(folderToWorkWith?.depthByFolderId ?? []), folderId]),
          folderEntity: FolderEntity.FOLDER,
          labels: categoryFromDepth.labels,
          updatedAt: fromDateTime(todayDateTime()),
          entityId: folderToWorkWith?.entityId ?? '',
        };
        foldersToAdd.push(newFolder);
        foldersToWorkWith[folderToWorkWithIndex] = newFolder;
        parentFolders[folderToWorkWithIndex] = newFolder;
      }
    }
  }
  const foldersToMove = await getFoldersWithCustomFilter((folder) =>
    folder.depthByFolderId.includes(currentCategoryFolder.id)
  );
  for (const oldParentFolder of oldParentFolders) {
    for (const parentFolder of parentFolders) {
      const toUpdate = foldersToMove.map((folder) => {
        //New depth
        const baseDepth = getFolderDepthForNavigation(parentFolder);
        const oldBaseDepth = currentCategoryFolder.depth;
        const depth = checkDepth(folder.depth.replace(oldBaseDepth, baseDepth));

        //New depthByFolderId
        const currentDepthByFolderId = [...folder.depthByFolderId];
        const indexOfOldParent = folder.depthByFolderId.indexOf(oldParentFolder.id);
        currentDepthByFolderId.splice(0, indexOfOldParent + 1);
        const depthByFolderId = checkDepthByFolderId([...parentFolder.depthByFolderId, ...currentDepthByFolderId]);

        //New depthString
        const currentDepthString = [...folder.depthString];
        currentDepthString.splice(0, indexOfOldParent);
        const newDepthString = checkDepthStringArray([...parentFolder.depthString, ...currentDepthString]);
        return {
          key: folder.id,
          changes: {
            depth,
            depthByFolderId: depthByFolderId,
            depthString: newDepthString,
          },
        };
      });

      foldersToUpdate.push(...toUpdate);
    }
    oldParentFoldersToCheck.push(oldParentFolder);
  }
  return {
    foldersToAdd,
    foldersToUpdate,
    oldParentFoldersToCheck,
  };
};

const checkDepth = (depth: string) => {
  const depthParts = depth.split('.');
  const uniqueDepthParts = [...new Set(depthParts)];
  return Array.from(uniqueDepthParts).join('.');
};

const checkDepthByFolderId = (depthByFolderId: string[]) => {
  const uniqueDepthByFolderId = [...new Set(depthByFolderId)];
  return Array.from(uniqueDepthByFolderId);
};

const checkDepthStringArray = (depthString: (string | CustomLabel[])[]): (string | CustomLabel[])[] => {
  const uniqueDepthString = [...new Set(depthString)];
  return Array.from(uniqueDepthString);
};

export const getCategoryDepthToAdd = async (
  currentCategory: DocumentCategoryWithoutReadIdAndClientId,
  currentEntityFolders: FolderDatabaseModel[],
  categoryDepth: DocumentCategoryWithoutReadIdAndClientId[],
  getDocumentCategoryById: FileCategoryContext['getDocumentCategoryById']
): Promise<{ categoryDepth: DocumentCategoryWithoutReadIdAndClientId[]; folderCategory?: FolderDatabaseModel[] }> => {
  const folderCategory = currentEntityFolders.filter(
    (folder) => folder.documentCategoryId === currentCategory.id && folder.folderEntity === FolderEntity.FOLDER
  );
  if (!isEmpty(folderCategory)) return { categoryDepth, folderCategory };

  if (currentCategory.parentId) {
    const newDepth = calculateCategoryDepth(currentCategory, [], getDocumentCategoryById);
    return { categoryDepth: newDepth };
  }
  return { categoryDepth: [...categoryDepth, currentCategory] };
};

export const calculateCategoryDepth = (
  currentCategory: DocumentCategoryWithoutReadIdAndClientId,
  categoryDepth: DocumentCategoryWithoutReadIdAndClientId[],
  getDocumentCategoryById: FileCategoryContext['getDocumentCategoryById']
): DocumentCategoryWithoutReadIdAndClientId[] => {
  if (isNil(currentCategory)) return [];
  const parentCategory = getDocumentCategoryById(currentCategory.parentId!);

  if (parentCategory) {
    return [...calculateCategoryDepth(parentCategory, categoryDepth, getDocumentCategoryById), currentCategory];
  }
  return [...categoryDepth, currentCategory];
};

export const syncAddFile = async (
  file: FileModel,
  charges: Charge[],
  fileCategories: DocumentCategoryWithoutReadIdAndClientId[],
  getFoldersByIndex: FolderDatabaseContext['getFoldersByIndex'],
  getTechnic: TechnicsContext['getTechnic'],
  addedFolders: FolderDatabaseModel[],
  clientId?: string
) => {
  const foldersToAdd: FolderDatabaseModel[] = [];
  if (isGeneratedUblDocument(file.mimeType)) {
    return foldersToAdd;
  }
  const documentFolder = await getFoldersByIndex(FolderIndexes.DOCUMENT_ID, file.id);
  if (documentFolder.some((folder) => folder.entityId === file.foreignKey)) {
    console.warn(`File ${file.id} already exists in foldersToAdd`);
    return foldersToAdd;
  }
  if (!AllowedFilesEntities.includes(file.foreignTableName as EntityType)) {
    console.warn(`Entity type ${file.foreignTableName} is not supported for file synchronization.`);
    return;
  }
  const foldersInEntity = await getFoldersByIndex(FolderIndexes.ENTITY_ID, file.foreignKey);

  const fileDocumentCategory = fileCategories.find((category) => category.id === file.categoryId);
  if (isNil(fileDocumentCategory)) {
    return foldersToAdd;
  }

  const targetFolders = [...foldersInEntity, ...addedFolders].filter((folderInEntity) => {
    return folderInEntity.documentCategoryId === file.categoryId && folderInEntity.folderEntity === FolderEntity.FOLDER;
  });

  if (!isNilOrEmpty(targetFolders)) {
    for (const targetFolder of targetFolders) {
      foldersToAdd.push(getFolderToAddWithTargetFolder(file, targetFolder, fileDocumentCategory.fileCategory));
    }
    return foldersToAdd;
  } else {
    const currentCategory = fileDocumentCategory;
    if (currentCategory?.fileCategory === FileCategory.CHARGE_INVOICE) {
      const unitOwners = await fetchUnitOwnersForFolders(getTableClientId(clientId!, UNIT_OWNER_TABLE_NAME));
      const chargesMap = calculateUniqueCharges(charges, unitOwners);
      for (const charge of [...chargesMap['buildingCharges'], ...chargesMap['unitCharges']]) {
        const currentEntityItems = await getFoldersByIndex(FolderIndexes.ENTITY_ID, charge.entityId!);
        const chargeFolders = calculateFoldersToAdd(currentCategory!, currentEntityItems, fileCategories, file);
        foldersToAdd.push(...chargeFolders);
      }
      return foldersToAdd;
    }

    if (file.foreignTableName === 'Technic') {
      const currentTechnic = getTechnic(file.foreignKey);
      const entityId = currentTechnic?.leaseId ?? currentTechnic?.unitId ?? currentTechnic?.buildingId;
      const currentEntityItems = await getFoldersByIndex(FolderIndexes.ENTITY_ID, entityId ?? '');
      const technicFolders = calculateFoldersToAdd(currentCategory!, currentEntityItems, fileCategories, file);
      foldersToAdd.push(...technicFolders);
      return foldersToAdd;
    }

    if (currentCategory?.fileCategory === FileCategory.INVOICE) {
      const currentEntity = await fetchInvoices('byId', file.foreignKey).then((invoices) => invoices[0]);
      if (!currentEntity || !currentEntity.leaseId) {
        return foldersToAdd;
      }
      const currentEntityItems = await getFoldersByIndex(FolderIndexes.ENTITY_ID, currentEntity.leaseId);
      const invoiceFolders = calculateFoldersToAdd(
        currentCategory!,
        currentEntityItems,
        fileCategories,
        file,
        currentEntity
      );
      foldersToAdd.push(...invoiceFolders);
      return foldersToAdd;
    }

    const currentEntityItems = await getFoldersByIndex(FolderIndexes.ENTITY_ID, file.foreignKey);

    const fileFolders = calculateFoldersToAdd(currentCategory!, currentEntityItems, fileCategories, file);
    foldersToAdd.push(...fileFolders);
    return foldersToAdd;
  }
};

export const syncDeleteFile = async (
  file: FileModel,
  getFoldersByIndex: FolderDatabaseContext['getFoldersByIndex'],
  getDocumentCategoryById: FileCategoryContext['getDocumentCategoryById'],
  getFoldersWithCustomFilter: FolderDatabaseContext['getFoldersWithCustomFilter']
) => {
  const foldersToDelete: string[] = [];
  const foldersToCheck: FolderDatabaseModel[] = [];
  const documentFolder = await getFoldersByIndex(FolderIndexes.DOCUMENT_ID, file.id).then((folders) => folders[0]);
  if (documentFolder) {
    foldersToDelete.push(documentFolder.id);
  }
  if (!documentFolder) {
    console.warn(`No folder found for file ${file.id}`);
    return { foldersToDelete, foldersToCheck };
  }
  //Need to check to delete categoryFolder
  const currentCategory = getDocumentCategoryById(documentFolder.documentCategoryId);
  const categoryDepth = calculateCategoryDepth(currentCategory, [], getDocumentCategoryById);
  for (const category of categoryDepth) {
    const categoryFolder = await getFoldersWithCustomFilter(
      (folder) =>
        folder.documentCategoryId === category.id &&
        folder.folderEntity === FolderEntity.FOLDER &&
        folder.entityId === documentFolder.entityId
    ).then((folders) => folders[0]);
    if (categoryFolder) {
      foldersToCheck.push(categoryFolder);
    }
  }
  return {
    foldersToDelete,
    foldersToCheck,
  };
};

export const checkForEmptyFoldersAfterAction = async (
  folderToCheck: FolderDatabaseModel,
  getDocumentCategoryById: FileCategoryContext['getDocumentCategoryById'],
  getFoldersWithCustomFilter: FolderDatabaseContext['getFoldersWithCustomFilter'],
  getFoldersByDepthRange: FolderDatabaseContext['getFoldersByDepthRange']
) => {
  if (folderToCheck.folderEntity === FolderEntity.FOLDER) {
    const oldParentCategory = getDocumentCategoryById(folderToCheck.documentCategoryId);
    const categoryDepth = calculateCategoryDepth(oldParentCategory, [], getDocumentCategoryById);
    for (const categoryToCheck of categoryDepth) {
      const folderCategory = await getDocumentCategoryFolder(
        folderToCheck.entityId,
        categoryToCheck.id,
        getFoldersWithCustomFilter
      );
      if (isNil(folderCategory)) continue;
      const foldersByDepth = await getFoldersByDepthRange(
        `${folderCategory.depth}.${folderCategory.documentCategoryId}`
      );
      const totalSize = getFolderSize(foldersByDepth);
      if (totalSize === 0) {
        return folderCategory.id;
      }
    }
  }
};

export const getFoldersAtBaseEntityLevel = async (
  entityId: string,
  getFoldersByIndex: FolderDatabaseContext['getFoldersByIndex']
) => {
  const allEntityFolders = await getFoldersByIndex(FolderIndexes.ENTITY_ID, entityId);
  const entityFolder = allEntityFolders.find(
    (folder) => ![FolderEntity.FILE, FolderEntity.FOLDER].includes(folder.folderEntity)
  );
  return allEntityFolders.filter(
    (folder) =>
      folder.depth === getFolderDepthForNavigation(entityFolder) && folder.folderEntity === FolderEntity.FOLDER
  );
};

export const getCategoryToWorkWith = async (
  lastFolderInNavigation: FolderDatabaseModel,
  currentLevelCategories: DocumentCategoryWithoutReadIdAndClientId[],
  createFileCategory: FileCategoryContext['createFileCategory'],
  getDocumentCategoryById: FileCategoryContext['getDocumentCategoryById'],
  labels?: CustomLabel[],
  targetFolderCategoryId?: string
): Promise<{ category: DocumentCategoryWithoutReadIdAndClientId; folder?: FolderDatabaseModel }> => {
  if (!isNilOrEmpty(labels) && !isNilOrEmpty(labels?.[0].label)) {
    const uniqueLabels = getUniqueCategoryParameter(labels!, currentLevelCategories);
    const newCategory = await createFileCategory(
      uniqueLabels,
      lastFolderInNavigation.categoryEntity as EntityType,
      targetFolderCategoryId
    );
    return { category: newCategory };
  }

  return { category: getDocumentCategoryById(targetFolderCategoryId!) };
};

export const getFolderEntityDepth = (
  folder: FolderDatabaseModel,
  getDocumentCategoryById: FileCategoryContext['getDocumentCategoryById']
): string => {
  const fullDepth = folder.depth.split('.');
  const entityDepth: string[] = [];

  for (const depthPart of fullDepth) {
    const category = getDocumentCategoryById(depthPart);
    if (!category) {
      entityDepth.push(depthPart);
    }
  }

  return entityDepth.join('.');
};

const checkFoldersInSync = async (
  foldersToCheck: FolderDatabaseModel[],
  getDocumentCategoryById: FileCategoryContext['getDocumentCategoryById'],
  getFoldersWithCustomFilter: FolderDatabaseContext['getFoldersWithCustomFilter'],
  getFoldersByDepthRange: FolderDatabaseContext['getFoldersByDepthRange'],
  bulkDeleteFolders: FolderDatabaseContext['bulkDeleteFolders']
) => {
  const foldersToDelete: string[] = [];
  for (const folderToCheck of foldersToCheck) {
    const folderId = await checkForEmptyFoldersAfterAction(
      folderToCheck,
      getDocumentCategoryById,
      getFoldersWithCustomFilter,
      getFoldersByDepthRange
    );
    if (!isNil(folderId)) {
      foldersToDelete.push(folderId);
    }
  }
  await bulkDeleteFolders(foldersToDelete);
};

export const syncFileChanges = async (
  file: FileModel,
  folderDatabaseAction: FolderDatabaseAction,
  syncId: string,
  charges: Charge[] | undefined,
  fileCategoriesToWorkWith: DocumentCategoryWithoutReadIdAndClientId[],
  getFoldersByIndex: FolderDatabaseContext['getFoldersByIndex'],
  getFoldersWithCustomFilter: FolderDatabaseContext['getFoldersWithCustomFilter'],
  getFoldersByDepthRange: FolderDatabaseContext['getFoldersByDepthRange'],
  getDocumentCategoryById: FileCategoryContext['getDocumentCategoryById'],
  getTechnic: TechnicsContext['getTechnic'],
  bulkAddFolders: FolderDatabaseContext['bulkAddFolders'],
  bulkDeleteFolders: FolderDatabaseContext['bulkDeleteFolders'],
  deleteItemsFromFoldersToSync: FolderDatabaseContext['deleteItemsFromFoldersToSync'],
  clientId?: string
) => {
  switch (folderDatabaseAction) {
    case FolderDatabaseAction.CREATE: {
      const existingFile = await getFoldersByIndex(FolderIndexes.DOCUMENT_ID, file.id);
      if (!isNilOrEmpty(existingFile)) {
        console.warn(`File with ID ${file.id} already exists in the database. Skipping creation.`);
        // await bulkDeleteFolders(existingFile.map((folder) => folder.id));
        break;
      }
      const newFoldersToAdd = await syncAddFile(
        file,
        charges ?? [],
        fileCategoriesToWorkWith,
        getFoldersByIndex,
        getTechnic,
        [],
        clientId ?? undefined
      );
      if (!isNilOrEmpty(newFoldersToAdd)) {
        await bulkAddFolders(newFoldersToAdd);
      }
      break;
    }
    case FolderDatabaseAction.DELETE: {
      const { foldersToDelete: newFoldersToDelete, foldersToCheck: newFoldersToCheck } = await syncDeleteFile(
        file,
        getFoldersByIndex,
        getDocumentCategoryById,
        getFoldersWithCustomFilter
      );
      await bulkDeleteFolders(newFoldersToDelete);
      await checkFoldersInSync(
        newFoldersToCheck,
        getDocumentCategoryById,
        getFoldersWithCustomFilter,
        getFoldersByDepthRange,
        bulkDeleteFolders
      );
      break;
    }
    case FolderDatabaseAction.UPDATE: {
      const { foldersToDelete: newFoldersToDelete, foldersToCheck: newFoldersToCheck } = await syncDeleteFile(
        file,
        getFoldersByIndex,
        getDocumentCategoryById,
        getFoldersWithCustomFilter
      );
      await bulkDeleteFolders(newFoldersToDelete);
      const newFoldersToAdd = await syncAddFile(
        file,
        charges ?? [],
        fileCategoriesToWorkWith,
        getFoldersByIndex,
        getTechnic,
        [],
        clientId ?? undefined
      );
      if (!isNilOrEmpty(newFoldersToAdd)) {
        await bulkAddFolders(newFoldersToAdd);
      }
      await checkFoldersInSync(
        newFoldersToCheck,
        getDocumentCategoryById,
        getFoldersWithCustomFilter,
        getFoldersByDepthRange,
        bulkDeleteFolders
      );
      break;
    }
  }
  await deleteItemsFromFoldersToSync([syncId]);
};

const checkOldCategoriesInSync = async (
  foldersToCheck: FolderDatabaseModel[],
  getDocumentCategoryById: FileCategoryContext['getDocumentCategoryById'],
  getFoldersWithCustomFilter: FolderDatabaseContext['getFoldersWithCustomFilter'],
  getFoldersByDepthRange: FolderDatabaseContext['getFoldersByDepthRange'],
  bulkDeleteFolders: FolderDatabaseContext['bulkDeleteFolders']
) => {
  const foldersToDelete: string[] = [];
  for (const oldParentFolder of foldersToCheck) {
    const folderId = await checkForEmptyFoldersAfterAction(
      oldParentFolder,
      getDocumentCategoryById,
      getFoldersWithCustomFilter,
      getFoldersByDepthRange
    );
    if (!isNil(folderId)) {
      foldersToDelete.push(folderId);
    }
  }
  if (!isEmpty(foldersToDelete)) {
    await bulkDeleteFolders(foldersToDelete);
  }
};

export const syncCategoryChanges = async (
  category: DocumentCategoryWithoutReadIdAndClientId,
  folderDatabaseAction: FolderDatabaseAction,
  syncId: string,
  getFoldersWithCustomFilter: FolderDatabaseContext['getFoldersWithCustomFilter'],
  getFoldersByIndex: FolderDatabaseContext['getFoldersByIndex'],
  getDocumentCategoryById: FileCategoryContext['getDocumentCategoryById'],
  updateDatabaseFolder: FolderDatabaseContext['updateDatabaseFolder'],
  bulkAddFolders: FolderDatabaseContext['bulkAddFolders'],
  bulkUpdateDatabaseFolders: FolderDatabaseContext['bulkUpdateDatabaseFolders'],
  deleteItemsFromFoldersToSync: FolderDatabaseContext['deleteItemsFromFoldersToSync'],
  getFoldersByDepthRange: FolderDatabaseContext['getFoldersByDepthRange'],
  bulkDeleteFolders: FolderDatabaseContext['bulkDeleteFolders']
) => {
  const currentCategoryFolders = await getFoldersWithCustomFilter(
    (folder) => folder.documentCategoryId === category.id && folder.folderEntity === FolderEntity.FOLDER
  );
  for (const currentCategoryFolder of currentCategoryFolders) {
    if (folderDatabaseAction === FolderDatabaseAction.UPDATE) {
      const updatedCategoryFolder: FolderDatabaseModel = {
        ...currentCategoryFolder,
        labels: category.labels,
      };
      await updateDatabaseFolder(updatedCategoryFolder);
      const {
        foldersToAdd: newFoldersToAdd,
        foldersToUpdate: newFoldersToUpdate,
        oldParentFoldersToCheck: newOldParentFoldersToCheck,
      } = await syncFolderMove(
        updatedCategoryFolder,
        category,
        getFoldersWithCustomFilter,
        getFoldersByIndex,
        getDocumentCategoryById
      );
      if (!isEmpty(newFoldersToAdd)) {
        await bulkAddFolders(newFoldersToAdd);
      }
      if (!isEmpty(newFoldersToUpdate)) {
        await bulkUpdateDatabaseFolders(newFoldersToUpdate);
      }
      if (!isEmpty(newOldParentFoldersToCheck)) {
        await checkOldCategoriesInSync(
          newOldParentFoldersToCheck,
          getDocumentCategoryById,
          getFoldersWithCustomFilter,
          getFoldersByDepthRange,
          bulkDeleteFolders
        );
      }
    }
  }
  await deleteItemsFromFoldersToSync([syncId]);
};
